import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlignLeft, AlignCenter, AlignRight, Bold, Italic, Upload, Loader2, Eye, EyeOff, Lock, Unlock, ChevronUp, ChevronDown, Trash2, Copy, Type, Image as ImageIcon, Square, Circle } from 'lucide-react';
import { Field, NumberInput, ColorInput, FontPicker, inputCls } from './EditorControls';
import { SHAPES, LINE_STYLES } from './shapes';
import { runsOf, hasRichColors, retargetRuns } from './richText';
import { Minus, Shapes, Group as GroupIcon, Ungroup as UngroupIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const uploadImage = async (file) => {
  const token = localStorage.getItem('token');
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(`${API}/admin/uploads/image`, form, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
};

const Toggle = ({ active, onClick, children, testId, title }) => (
  <button
    type="button"
    title={title}
    data-testid={testId}
    onClick={onClick}
    className={`flex h-9 flex-1 items-center justify-center rounded-lg border text-sm transition-colors ${
      active ? 'border-[#A855F7] bg-[#A855F7]/20 text-white' : 'border-white/[0.08] text-[#a1a1aa] hover:text-white'
    }`}
  >
    {children}
  </button>
);

const ImageReplace = ({ onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadImage(file);
      onChange(res.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" data-testid="replace-image-btn" onClick={() => ref.current?.click()} disabled={busy}
        className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-white/[0.15] text-sm text-[#a1a1aa] hover:border-[#EAB308] hover:text-[#EAB308]">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Replace image
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} />
    </>
  );
};

export const PropertiesPanel = ({ el, canvas, onChange, onCanvasChange, fonts }) => {
  if (!el) {
    return (
      <div className="space-y-4 p-4" data-testid="canvas-properties">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-[#71717a]">Page</p>
        <Field label="Background colour"><ColorInput value={canvas.background} onChange={(v) => onCanvasChange({ background: v })} testId="canvas-bg-color" /></Field>
        <Field label="Page height (px)"><NumberInput value={canvas.height} min={400} max={6000} step={10} onChange={(v) => onCanvasChange({ height: Math.max(400, Math.min(6000, v)) })} testId="canvas-height-input" /></Field>
        <p className="text-xs leading-relaxed text-[#71717a]">Width is fixed at {canvas.width}px (a phone screen). Members scroll down for taller pages.</p>
      </div>
    );
  }

  const set = (patch) => onChange(patch);

  return (
    <div className="space-y-4 p-4" data-testid="element-properties">
      <Field label="Name"><input className={inputCls} value={el.name || ''} onChange={(e) => set({ name: e.target.value })} data-testid="element-name-input" /></Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="X"><NumberInput value={el.x} onChange={(v) => set({ x: v })} testId="prop-x" /></Field>
        <Field label="Y"><NumberInput value={el.y} onChange={(v) => set({ y: v })} testId="prop-y" /></Field>
        <Field label="Width"><NumberInput value={el.w} min={1} onChange={(v) => set({ w: Math.max(1, v) })} testId="prop-w" /></Field>
        <Field label="Height"><NumberInput value={el.h} min={1} onChange={(v) => set({ h: Math.max(1, v) })} testId="prop-h" /></Field>
        <Field label="Rotation"><NumberInput value={el.rotation || 0} onChange={(v) => set({ rotation: v })} testId="prop-rotation" /></Field>
        <Field label="Opacity"><NumberInput value={el.opacity ?? 1} min={0} max={1} step={0.05} onChange={(v) => set({ opacity: Math.max(0, Math.min(1, v)) })} testId="prop-opacity" /></Field>
      </div>

      {el.type === 'text' && (
        <>
          <Field label="Text">
            <textarea className={`${inputCls} h-auto min-h-[72px] py-2`} rows={3} value={el.text} onChange={(e) => set(hasRichColors(el) ? { text: e.target.value, runs: retargetRuns(runsOf(el), e.target.value) } : { text: e.target.value })} data-testid="prop-text" />
          </Field>
          <Field label="Font"><FontPicker value={el.fontFamily} onChange={(v) => set({ fontFamily: v })} fonts={fonts} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Size"><NumberInput value={el.fontSize} min={6} onChange={(v) => set({ fontSize: Math.max(6, v) })} testId="prop-font-size" /></Field>
            <Field label="Weight">
              <select className={inputCls} value={el.fontWeight} onChange={(e) => set({ fontWeight: parseInt(e.target.value, 10) })} data-testid="prop-font-weight">
                {[300, 400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </Field>
            <Field label="Line height"><NumberInput value={el.lineHeight} step={0.05} min={0.5} onChange={(v) => set({ lineHeight: v })} /></Field>
            <Field label="Letter spacing"><NumberInput value={el.letterSpacing || 0} step={0.5} onChange={(v) => set({ letterSpacing: v })} /></Field>
          </div>
          <Field label={hasRichColors(el) ? 'Base colour' : 'Colour'}><ColorInput value={el.color} onChange={(v) => set({ color: v })} testId="prop-text-color" /></Field>
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5" data-testid="word-colour-help">
            <p className="text-[11px] leading-relaxed text-[#a1a1aa]">
              <span className="font-semibold text-white">Colour single words:</span> double-tap the text on the canvas, highlight a word, then tap a colour in the floating bar.
            </p>
            {hasRichColors(el) && (
              <button type="button" data-testid="reset-word-colours-btn" onClick={() => set({ runs: undefined })} className="mt-2 text-[11px] font-semibold text-[#EAB308] hover:underline">
                Reset word colours to base colour
              </button>
            )}
          </div>
          <div className="flex gap-1.5">
            <Toggle active={el.align === 'left'} onClick={() => set({ align: 'left' })} title="Align left"><AlignLeft className="h-4 w-4" /></Toggle>
            <Toggle active={el.align === 'center'} onClick={() => set({ align: 'center' })} title="Align centre"><AlignCenter className="h-4 w-4" /></Toggle>
            <Toggle active={el.align === 'right'} onClick={() => set({ align: 'right' })} title="Align right"><AlignRight className="h-4 w-4" /></Toggle>
            <Toggle active={el.fontWeight >= 700} onClick={() => set({ fontWeight: el.fontWeight >= 700 ? 400 : 700 })} title="Bold"><Bold className="h-4 w-4" /></Toggle>
            <Toggle active={el.fontStyle === 'italic'} onClick={() => set({ fontStyle: el.fontStyle === 'italic' ? 'normal' : 'italic' })} title="Italic"><Italic className="h-4 w-4" /></Toggle>
          </div>
          <div className="flex gap-1.5">
            <Toggle active={!!el.uppercase} onClick={() => set({ uppercase: !el.uppercase })} title="Uppercase"><span className="text-xs font-semibold">AA</span></Toggle>
            <Toggle active={!!el.shadow} onClick={() => set({ shadow: !el.shadow })} title="Shadow"><span className="text-xs font-semibold">Shadow</span></Toggle>
          </div>
        </>
      )}

      {el.type === 'image' && (
        <>
          <ImageReplace onChange={(src) => set({ src })} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Fit">
              <select className={inputCls} value={el.fit} onChange={(e) => set({ fit: e.target.value })}>
                <option value="cover">Cover (crop)</option>
                <option value="contain">Contain</option>
                <option value="fill">Stretch</option>
              </select>
            </Field>
            <Field label="Corner radius"><NumberInput value={el.radius || 0} min={0} onChange={(v) => set({ radius: Math.max(0, v) })} /></Field>
          </div>
        </>
      )}

      {el.type === 'line' && (
        <>
          <Field label="Line colour"><ColorInput value={el.stroke} onChange={(v) => set({ stroke: v })} testId="prop-line-color" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Thickness"><NumberInput value={el.strokeWidth || 3} min={1} onChange={(v) => set({ strokeWidth: Math.max(1, v) })} testId="prop-line-width" /></Field>
            <Field label="Style">
              <select className={inputCls} value={el.dash || 'solid'} onChange={(e) => set({ dash: e.target.value })} data-testid="prop-line-style">
                {LINE_STYLES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Ends">
            <select className={inputCls} value={el.cap || 'round'} onChange={(e) => set({ cap: e.target.value })}>
              <option value="round">Rounded</option>
              <option value="butt">Square</option>
            </select>
          </Field>
          <p className="text-xs text-[#71717a]">Tip: rotate the line for diagonals; drag the side handles to change its length.</p>
        </>
      )}

      {el.type === 'shape' && (
        <>
          <Field label="Shape">
            <select className={inputCls} value={el.shape} onChange={(e) => set({ shape: e.target.value, name: SHAPES[e.target.value]?.label || 'Shape' })} data-testid="prop-shape-kind">
              {Object.entries(SHAPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Fill"><ColorInput value={el.fill} onChange={(v) => set({ fill: v })} testId="prop-fill" /></Field>
          <Field label="Border colour"><ColorInput value={el.stroke} onChange={(v) => set({ stroke: v })} /></Field>
          <Field label="Border width"><NumberInput value={el.strokeWidth || 0} min={0} onChange={(v) => set({ strokeWidth: Math.max(0, v) })} /></Field>
        </>
      )}

      {(el.type === 'rect' || el.type === 'ellipse') && (
        <>
          <Field label="Fill"><ColorInput value={el.fill} onChange={(v) => set({ fill: v })} testId="prop-fill" /></Field>
          <Field label="Border colour"><ColorInput value={el.stroke} onChange={(v) => set({ stroke: v })} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Border width"><NumberInput value={el.strokeWidth || 0} min={0} onChange={(v) => set({ strokeWidth: Math.max(0, v) })} /></Field>
            {el.type === 'rect' && <Field label="Corner radius"><NumberInput value={el.radius || 0} min={0} onChange={(v) => set({ radius: Math.max(0, v) })} /></Field>}
          </div>
        </>
      )}
    </div>
  );
};

const ICONS = { text: Type, image: ImageIcon, rect: Square, ellipse: Circle, shape: Shapes, line: Minus };

const GROUP_COLORS = ['#A855F7', '#EAB308', '#10b981', '#3b82f6', '#f43f5e', '#f97316', '#06b6d4'];
const groupColor = (gid) => {
  if (!gid) return 'transparent';
  let h = 0;
  for (let i = 0; i < gid.length; i += 1) h = (h * 31 + gid.charCodeAt(i)) >>> 0;
  return GROUP_COLORS[h % GROUP_COLORS.length];
};

export const LayersPanel = ({ elements, selectedIds = [], onSelect, onToggleSelect, onChange, onReorder, onDelete, onDuplicate, canGroup, canUngroup, onGroup, onUngroup }) => {
  const ordered = [...elements].reverse();
  return (
    <div className="p-2" data-testid="layers-panel">
      <div className="flex items-center justify-between px-2 pb-2 pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-[#71717a]">Layers · {elements.length}</p>
        {(canGroup || canUngroup) && (
          <div className="flex items-center gap-1">
            {canGroup && <button type="button" data-testid="layers-group-btn" onClick={onGroup} title="Group" className="flex items-center gap-1 rounded-md bg-[#A855F7]/20 px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#A855F7]/30"><GroupIcon className="h-3 w-3" /> Group</button>}
            {canUngroup && <button type="button" data-testid="layers-ungroup-btn" onClick={onUngroup} title="Ungroup" className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-[#a1a1aa] hover:text-white"><UngroupIcon className="h-3 w-3" /> Ungroup</button>}
          </div>
        )}
      </div>
      {elements.length === 0 && <p className="px-2 py-4 text-center text-xs text-[#52525b]">Add text, images or shapes from the toolbar.</p>}
      <div className="space-y-0.5">
        {ordered.map((el, i) => {
          const Icon = ICONS[el.type] || Square;
          const active = selectedIds.includes(el.id);
          const idx = elements.length - 1 - i;
          return (
            <div
              key={el.id}
              onClick={(e) => (e.shiftKey ? onToggleSelect(el.id) : onSelect(el.id))}
              data-testid={`layer-${el.id}`}
              className={`group flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${active ? 'bg-[#A855F7]/20 text-white' : 'text-[#a1a1aa] hover:bg-white/[0.04]'}`}
              style={el.groupId ? { boxShadow: `inset 3px 0 0 ${groupColor(el.groupId)}` } : undefined}
            >
              <input
                type="checkbox"
                checked={active}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleSelect(el.id)}
                data-testid={`layer-check-${el.id}`}
                className="h-3.5 w-3.5 shrink-0 accent-[#A855F7]"
              />
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate text-xs">{el.name || el.type}{el.type === 'text' && el.text ? ` · ${el.text.slice(0, 18)}` : ''}</span>
              <div className={`items-center gap-0.5 ${active ? 'flex' : 'hidden group-hover:flex'}`}>
                <button type="button" title="Up" onClick={(e) => { e.stopPropagation(); onReorder(el.id, 1); }} disabled={idx === elements.length - 1} className="rounded p-1 hover:text-white disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                <button type="button" title="Down" onClick={(e) => { e.stopPropagation(); onReorder(el.id, -1); }} disabled={idx === 0} className="rounded p-1 hover:text-white disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                <button type="button" title={el.hidden ? 'Show' : 'Hide'} onClick={(e) => { e.stopPropagation(); onChange(el.id, { hidden: !el.hidden }); }} className="rounded p-1 hover:text-white">{el.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
                <button type="button" title={el.locked ? 'Unlock' : 'Lock'} onClick={(e) => { e.stopPropagation(); onChange(el.id, { locked: !el.locked }); }} className="rounded p-1 hover:text-white">{el.locked ? <Lock className="h-3.5 w-3.5 text-[#EAB308]" /> : <Unlock className="h-3.5 w-3.5" />}</button>
                <button type="button" title="Duplicate" onClick={(e) => { e.stopPropagation(); onDuplicate(el.id); }} className="rounded p-1 hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
                <button type="button" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(el.id); }} className="rounded p-1 hover:text-[#f43f5e]" data-testid={`layer-delete-${el.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
